import { useQuery } from '@apollo/client/react';
import { DimeButton, DimeCardShellSkeleton, DimeDetailPageStates } from '@nudool/design-system';
import { createFileRoute, Link } from '@tanstack/react-router';
import { TruckIcon } from 'lucide-react';
import { useBreadcrumbLabel } from '../../components/BreadcrumbLabelsContext.js';
import { assertUuidRouteParams } from '../../lib/assert-uuid-route-params.js';
import { usePermissions } from '../../modules/iam/permissions-context.js';
import {
  GET_VENDOR,
  type GetVendorQueryResult,
  type GetVendorQueryVariables,
} from '../../modules/vendors/operations.js';
import { VendorDetailView } from '../../modules/vendors/vendor-detail.js';

export const Route = createFileRoute('/vendors/$vendorId')({
  beforeLoad: ({ params }) => assertUuidRouteParams(params),
  component: VendorDetailPage,
});

// eslint-disable-next-line react-refresh/only-export-components
function VendorDetailPage() {
  const { vendorId } = Route.useParams();
  const { permissions } = usePermissions();
  const { data, loading, error, refetch } = useQuery<GetVendorQueryResult, GetVendorQueryVariables>(
    GET_VENDOR,
    { variables: { id: vendorId, canViewUsers: permissions.includes('user:view') } },
  );
  useBreadcrumbLabel(vendorId, data?.vendor?.name);

  return (
    <DimeDetailPageStates
      data={data?.vendor}
      loading={loading}
      error={error}
      entityLabel="Vendor"
      notFoundIcon={<TruckIcon />}
      notFoundAction={
        <DimeButton variant="outline" asChild>
          <Link to="/vendors">Back to vendors</Link>
        </DimeButton>
      }
      skeleton={<DimeCardShellSkeleton factCount={0} actionCount={3} tabCount={2} rowCount={5} />}
      onRetry={() => void refetch().catch(() => undefined)}
    >
      {(vendor) => <VendorDetailView vendor={vendor} onChanged={() => void refetch()} />}
    </DimeDetailPageStates>
  );
}
